'use client';

import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import {
  AlertTriangle,
  Bell,
  BellOff,
  CheckCheck,
  ExternalLink,
  MessageSquare,
  Swords,
  Trash2,
  Eye,
  EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn, truncateAddress } from '@/lib/utils';
import { useExternalLinkGuard } from '../ui/ExternalLinkGuard';
import { useWallet } from '@/hooks/use-wallet';
import {
  useNotifications,
  type Notification as LiveNotification,
} from '@/hooks/use-notifications';
import { useNotificationStream } from '@/hooks/use-notification-stream';
import { usePushNotifications } from '@/hooks/use-push-notifications';
import { useNotificationPreferences } from '@/hooks/use-notification-preferences';
import {
  PushBetaNoticeModal,
  usePushBetaNoticeGate,
} from '@/components/modals/push-beta-notice-modal';
import { generateGridAvatarUrl } from '@/lib/avatar';
import { getChatRoomDisplayName } from '@/lib/chat-rooms';
import { notificationHref } from '@/lib/notification-href';

const chainId = process.env.NEXT_PUBLIC_CHAIN_ID;
const explorerUrl =
  chainId === '8453' ? 'https://basescan.org' : 'https://sepolia.basescan.org';

/**
 * Classic notification taxonomy. Mirrors the production
 * `notification-bell.tsx` in `/components/layout` so we render the same
 * shapes (buyout / mention / expiry) but in the lobby's dense theme.
 */
type NotificationKind = 'buyout' | 'mention' | 'expiry_1d' | 'expiry_1h';

/** Push channel preference keys — grouped so "expiry" covers both 1d and 1h. */
type PushKind = 'buyouts' | 'mentions' | 'expiry';

const DEFAULT_PUSH_KINDS: Record<PushKind, boolean> = {
  buyouts: true,
  mentions: true,
  expiry: true,
};

const PUSH_KIND_META: Record<
  PushKind,
  { label: string; hint: string; icon: React.ReactNode; color: string }
> = {
  buyouts: {
    label: 'Buyouts',
    hint: 'Someone buys your seat',
    icon: <Swords className="h-3 w-3" />,
    color: 'text-rose-300',
  },
  mentions: {
    label: 'Mentions',
    hint: 'You were @-tagged in chat',
    icon: <MessageSquare className="h-3 w-3" />,
    color: 'text-sky-300',
  },
  expiry: {
    label: 'Expiry warnings',
    hint: '24h & 1h runway alerts',
    icon: <AlertTriangle className="h-3 w-3" />,
    color: 'text-orange-300',
  },
};

interface BoardRef {
  /** Pool / room identifier — feeds the deterministic generated avatar
   *  fallback when the board has no curated `pool_image_url`. Optional
   *  because the guest-mode mock entries don't carry a real id. */
  id?: string | null;
  name: string;
  hue: string;
  imageUrl?: string | null;
}

interface LobbyNotification {
  id: string;
  numericId?: number;
  kind: NotificationKind;
  board: BoardRef;
  /** Individual seat (for single buyout / expiry). */
  seat?: string;
  /** Seat list for bulk buyouts. */
  seats?: string[];
  /** "+$X.XX" paid when a buyer takes your seat. */
  earned?: string;
  /** The price paid by the buyer. */
  paid?: string;
  /** Display handle of the buyer or mentioner. */
  actor?: string;
  /** The chat message body for a mention. */
  message?: string;
  /** External link out (block explorer tx for buyouts). */
  href?: string;
  /** In-app navigation target (board page deep link). Clicking the row
   *  routes here and marks the notification as read. */
  navHref?: string;
  ago: string;
  unread?: boolean;
}

const MOCK: LobbyNotification[] = [
  {
    id: 'n1',
    kind: 'buyout',
    board: { id: 'mock-boardroom', name: 'The Boardroom', hue: '#ef4444' },
    seat: '017',
    actor: 'fortuna.eth',
    earned: '+$1.84',
    paid: '$184.00',
    ago: '2m',
  },
  {
    id: 'n2',
    kind: 'expiry_1h',
    board: { id: 'mock-alpha', name: 'Alpha Fund', hue: '#38bdf8' },
    seat: '013',
    ago: '12m',
  },
  {
    id: 'n3',
    kind: 'mention',
    board: { id: 'mock-starforge', name: 'Starforge', hue: '#a855f7' },
    actor: 'legend.eth',
    message: 'nice call on seat 013 — covering it for the round',
    ago: '42m',
  },
  {
    id: 'n4',
    kind: 'buyout',
    board: { id: 'mock-arcadia', name: 'Arcadia', hue: '#f59e0b' },
    seats: ['026', '027', '028', '031'],
    actor: 'whalewhisperer',
    earned: '+$12.42',
    paid: '$1,240.00',
    ago: '1h',
  },
  {
    id: 'n5',
    kind: 'expiry_1d',
    board: { id: 'mock-boardroom', name: 'The Boardroom', hue: '#ef4444' },
    seat: '009',
    ago: '4h',
  },
];

// ─── Utility helpers ───────────────────────────────────────────────────────

/** Stable hue derived from a string — gives each pool a distinct pill colour. */
function hueFor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }
  const hue = h % 360;
  return `hsl(${hue}, 70%, 60%)`;
}

function formatTimeAgo(timestamp: number): string {
  const now = Math.floor(Date.now() / 1000);
  const diff = Math.max(0, now - timestamp);
  if (diff < 60) return 'now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return `${Math.floor(diff / 604800)}w`;
}

/**
 * Adapt the live `Notification` shape from `useNotifications` into the
 * lobby's row-friendly `LobbyNotification` view model.
 */
function liveToLobby(n: LiveNotification): LobbyNotification | null {
  const poolLabel =
    n.pool_symbol && !n.pool_symbol.startsWith('0x')
      ? n.pool_symbol
      : 'Pool';
  const board: BoardRef = {
    id: n.pool_id ?? null,
    name: poolLabel,
    hue: hueFor(n.pool_id || poolLabel),
    imageUrl: n.pool_image_url ?? null,
  };
  const ago = formatTimeAgo(n.created_at);
  // In-app destination shared by every kind: clicking the row should
  // jump straight to the affected seat / chat message on the board.
  const navHref = notificationHref(n) ?? undefined;

  if (n.type === 'bought_out') {
    const amountUsd = n.amount ? Number(n.amount) / 1_000_000 : 0;
    const earnedFormatted = amountUsd > 0
      ? `+${amountUsd >= 100 ? `$${amountUsd.toFixed(0)}` : `$${amountUsd.toFixed(2)}`}`
      : undefined;
    const seatList = n.seat_indices ?? null;
    const isBulk = !!seatList && seatList.length > 1;
    return {
      id: `live-${n.id}`,
      numericId: n.id,
      kind: 'buyout',
      board,
      seat:
        !isBulk && n.seat_index != null
          ? String((n.seat_index ?? 0) + 1).padStart(3, '0')
          : undefined,
      seats: isBulk
        ? seatList!.map((i) => String((i ?? 0) + 1).padStart(3, '0'))
        : undefined,
      earned: earnedFormatted,
      actor: n.buyer_address ? truncateAddress(n.buyer_address) : undefined,
      // `href` here stays the external block-explorer tx link rendered
      // as a small "tx ↗" button on the right of the row. The row's
      // primary click target uses `navHref` instead so the two destinations
      // can coexist without one stealing the other's click.
      href: n.tx_hash ? `${explorerUrl}/tx/${n.tx_hash}` : undefined,
      navHref,
      ago,
      unread: !n.read,
    };
  }
  if (n.type === 'expiry_1d' || n.type === 'expiry_1h') {
    return {
      id: `live-${n.id}`,
      numericId: n.id,
      kind: n.type,
      board,
      seat:
        n.seat_index != null
          ? String((n.seat_index ?? 0) + 1).padStart(3, '0')
          : undefined,
      navHref,
      ago,
      unread: !n.read,
    };
  }
  if (n.type === 'chat_mention') {
    return {
      id: `live-${n.id}`,
      numericId: n.id,
      kind: 'mention',
      board: {
        // `pool_image_url` is keyed off the *board* the chat room
        // belongs to (server stamps it from the parent grid's metadata
        // on insert), so it's the right image even though the row is
        // a chat mention rather than a buyout. Fall back to a
        // generated avatar derived from the room id.
        id: n.room ?? n.pool_id ?? null,
        name: getChatRoomDisplayName(n.room),
        hue: hueFor(n.room ?? n.pool_id ?? 'mention'),
        imageUrl: n.pool_image_url ?? null,
      },
      actor: n.sender_address
        ? truncateAddress(n.sender_address)
        : n.title || 'someone',
      message: n.message,
      navHref,
      ago,
      unread: !n.read,
    };
  }
  return null;
}

// ─── Component ─────────────────────────────────────────────────────────────

export interface NotificationsDropdownProps {
  /**
   * Visual variant for the trigger button.
   *  - `'default'` (LobbyNav, desktop top bar): the dense 28×28 framed
   *    bell tile that lines up with the rest of the lobby chrome.
   *  - `'compact-badge'` (legacy mobile experiment, currently unused):
   *    a small unframed bell sized to overlay the Profile-tab avatar
   *    like a notification badge.
   *  - `'footer-pill'` (LobbyFooter, primary mobile entry): a
   *    chip that mirrors the `AudioToggle` styling so it sits
   *    flush next to the SFX / Music pills in the footer strip.
   *    Pairs naturally with `side="top"` so the dropdown opens
   *    upward away from the bottom-edge nav.
   */
  variant?: 'default' | 'compact-badge' | 'footer-pill';
  /**
   * Side the dropdown content opens toward. Forwarded straight to
   * Radix's `DropdownMenu.Content`. Defaults to `'bottom'` to keep
   * the desktop LobbyNav behaviour; the LobbyFooter pill passes
   * `'top'` so the panel "drops up" away from the bottom chrome.
   */
  side?: 'top' | 'bottom' | 'left' | 'right';
  /**
   * Alignment of the dropdown content relative to its trigger.
   * Defaults to `'end'` to match the existing top-bar placement;
   * the footer pill overrides to `'start'` so the panel hugs the
   * left edge of the trigger (room to fan rightward across the
   * bottom strip).
   */
  align?: 'start' | 'center' | 'end';
}

export function NotificationsDropdown({
  variant = 'default',
  side = 'bottom',
  align = 'end',
}: NotificationsDropdownProps = {}) {
  const router = useRouter();
  const { isConnected, isAddressLinked, connectWallet } = useWallet();
  const {
    notifications: liveNotifications,
    unreadCount: liveUnread,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();
  // Keep the realtime SSE channel alive while the dropdown is mounted.
  useNotificationStream();

  const push = usePushNotifications();
  const { preferences: serverPrefs, setPreference } = useNotificationPreferences();
  const isLive = isConnected;

  // First-time push beta acknowledgment. Surfaced before the browser's
  // permission prompt so the "best-effort delivery" framing lands
  // before the (one-shot) `Notification.requestPermission()` call —
  // see `PushBetaNoticeModal` for the localStorage gate. The gate is a
  // no-op once accepted, so returning users skip straight to the
  // browser prompt.
  const { gate: gatePushBeta, modalProps: pushBetaModalProps } =
    usePushBetaNoticeGate();

  // When a user taps "Enable push" without a linked wallet we open the
  // Privy connect modal and mark the subscribe as pending. The effect
  // below resumes the subscribe step once Privy finishes linking.
  const pendingEnableRef = useRef(false);

  const liveItems = useMemo(
    () =>
      liveNotifications
        .map(liveToLobby)
        .filter((n): n is LobbyNotification => n !== null),
    [liveNotifications],
  );

  // Guest-mode fallback list — only used when no wallet is connected so
  // the lobby still shows a populated bell for first-time visitors.
  const [mockItems, setMockItems] = useState(MOCK);

  /** Per-kind preferences from `/api/notifications/preferences` (same as classic app). */
  const pushKinds: Record<PushKind, boolean> = {
    buyouts: serverPrefs?.buyouts ?? DEFAULT_PUSH_KINDS.buyouts,
    mentions: serverPrefs?.chat_mentions ?? DEFAULT_PUSH_KINDS.mentions,
    expiry: serverPrefs?.forfeit_warnings ?? DEFAULT_PUSH_KINDS.expiry,
  };

  const items = isLive ? liveItems : mockItems;
  const unread = isLive ? liveUnread : items.filter((i) => i.unread).length;

  const runSubscribe = useCallback(async () => {
    const result = await push.requestPermission();
    if (result.ok) {
      toast.success('Push notifications enabled');
      return;
    }
    switch (result.reason) {
      case 'denied':
        toast.error('Push permission denied', {
          description: 'Enable notifications for this site in your browser settings.',
        });
        break;
      case 'server':
        toast.error('Could not save your subscription. Try again.');
        break;
      case 'unsupported':
        toast.error('Push is not supported in this browser');
        break;
      case 'no-wallet':
        // Shouldn't normally reach here — callers gate on `isAddressLinked`.
        toast.error('Connect a wallet first to enable push.');
        break;
      default:
        toast.error('Could not enable push notifications.');
    }
  }, [push]);

  const togglePush = useCallback(async () => {
    if (push.isLoading) return;

    if (push.isSubscribed) {
      const result = await push.unsubscribe();
      if (result.ok) {
        toast.success('Push notifications disabled');
      } else if (result.reason !== 'no-wallet') {
        toast.error('Could not disable push notifications.');
      }
      return;
    }

    if (!isConnected || !isAddressLinked) {
      pendingEnableRef.current = true;
      connectWallet();
      toast('Connect your wallet to enable push notifications.');
      return;
    }

    gatePushBeta(runSubscribe);
  }, [push, isConnected, isAddressLinked, connectWallet, runSubscribe, gatePushBeta]);

  // Resume the subscribe flow once Privy finishes linking the wallet.
  // `push.isLoading` guards against racing the initial permission prompt.
  // The first-time push-beta gate runs here too: a viewer who connected
  // their wallet specifically to enable push gets the same disclaimer
  // surface as one who already had a wallet linked.
  useEffect(() => {
    if (!pendingEnableRef.current) return;
    if (!isConnected || !isAddressLinked) return;
    if (push.isLoading) return;
    pendingEnableRef.current = false;
    gatePushBeta(runSubscribe);
  }, [isConnected, isAddressLinked, push.isLoading, runSubscribe, gatePushBeta]);

  const togglePushKind = (kind: PushKind) => {
    if (!isLive) return;
    const on = pushKinds[kind];
    const next = !on;
    if (kind === 'buyouts') setPreference('buyouts', next);
    else if (kind === 'mentions') setPreference('chat_mentions', next);
    else setPreference('forfeit_warnings', next);
  };

  const handleMarkAllRead = useCallback(() => {
    if (isLive) {
      markAllAsRead();
    } else {
      setMockItems((prev) => prev.map((i) => ({ ...i, unread: false })));
    }
  }, [isLive, markAllAsRead]);

  const handleMarkRead = useCallback(
    (id: string, numericId?: number) => {
      if (isLive && numericId != null) {
        markAsRead(numericId);
      } else if (!isLive) {
        setMockItems((prev) => prev.map((i) => (i.id === id ? { ...i, unread: false } : i)));
      }
    },
    [isLive, markAsRead],
  );

  const handleMarkUnread = useCallback(
    (id: string, numericId?: number) => {
      if (isLive && numericId != null) {
        markAsUnread(numericId);
      } else if (!isLive) {
        setMockItems((prev) => prev.map((i) => (i.id === id ? { ...i, unread: true } : i)));
      }
    },
    [isLive, markAsUnread],
  );

  const handleDelete = useCallback(
    (id: string, numericId?: number) => {
      if (isLive && numericId != null) {
        deleteNotification(numericId);
      } else if (!isLive) {
        setMockItems((prev) => prev.filter((i) => i.id !== id));
      }
    },
    [isLive, deleteNotification],
  );

  const pushIsOn = push.isSubscribed && push.permission === 'granted';
  const pushBlocked = push.permission === 'denied';
  const pushUnsupported = !push.isSupported && !push.isIosNonPwa;

  // Auto-mark on open: opening the dropdown is the user's "I've seen
  // these" gesture, matching the convention every other notification UI
  // (Slack, Discord, GitHub) uses. We fire `markAllAsRead()` on the
  // closed→open edge only — re-opening while already marked is a noop
  // server-side, but skipping the call avoids spurious cache
  // invalidations that would re-fetch the list mid-render.
  //
  // Auto-mark is debounced via `markedOnOpenRef` so a quick
  // open→close→open sequence doesn't double-fire while the previous
  // PATCH is still in flight.
  const markedOnOpenRef = useRef(false);
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        markedOnOpenRef.current = false;
        return;
      }
      if (markedOnOpenRef.current) return;
      markedOnOpenRef.current = true;
      // Only call the server when there's actually unread state to
      // change — keeps the mock/guest path clean and avoids a 200 ping
      // on every bell click.
      if (unread <= 0) return;
      handleMarkAllRead();
    },
    [unread, handleMarkAllRead],
  );

  return (
    <>
    <DropdownMenu.Root onOpenChange={handleOpenChange}>
      <DropdownMenu.Trigger asChild>
        {variant === 'footer-pill' ? (
          // LobbyFooter pill: matches the AudioToggle chip footprint
          // (`h-5 px-1.5 rounded-[3px] border`) so it slots in next to
          // SFX / Music without breaking the rhythm of the strip.
          // Tinted amber when there are unread alerts so the pill
          // reads as "live" without needing a separate dot — the count
          // chip on the right backs that up with an exact number.
          <button
            type="button"
            aria-label="Alerts"
            title={unread > 0 ? `Alerts · ${unread} unread` : 'Alerts'}
            className={cn(
              'flex items-center gap-1.5 px-1.5 h-5 rounded-[3px] border transition-colors shrink-0',
              unread > 0
                ? 'border-amber-400/40 bg-amber-500/10 text-amber-100 hover:border-amber-300/60'
                : 'border-white/10 bg-transparent text-zinc-400 hover:text-amber-100 hover:border-amber-400/30',
            )}
          >
            <span className="relative inline-flex">
              <Bell className="h-2.5 w-2.5" />
              {unread > 0 && (
                <span
                  aria-hidden
                  className="absolute -top-1 -right-1.5 h-2 w-2 rounded-full bg-red-500 shadow-[0_0_4px_rgba(239,68,68,0.7)]"
                />
              )}
            </span>
            <span className="text-[9px] tracking-[0.2em]">Alerts</span>
          </button>
        ) : variant === 'compact-badge' ? (
          // Legacy badge variant: an unframed bell sized to overlay
          // an avatar. Kept for backwards compatibility with any
          // surface that may still consume it; current production
          // surfaces use `'default'` (top-bar) and `'footer-pill'`
          // (mobile primary).
          <button
            type="button"
            aria-label="Notifications"
            title="Notifications"
            className="relative inline-flex h-5 w-5 items-center justify-center rounded-full text-zinc-200 hover:text-amber-100 transition-colors"
          >
            <Bell className="h-3 w-3" />
            {unread > 0 && (
              <span
                aria-hidden
                className="absolute -top-1 -right-1 inline-flex h-3 min-w-[12px] px-[2px] items-center justify-center rounded-full bg-red-500 text-white text-[7px] font-bold font-mono shadow-[0_0_4px_rgba(239,68,68,0.8)] ring-1 ring-black/70"
              >
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </button>
        ) : (
          <button
            type="button"
            aria-label="Notifications"
            title="Notifications (N)"
            className={cn(
              'relative h-7 w-7 flex items-center justify-center rounded-[4px] border transition-colors',
              'bg-black/30 hover:bg-white/5 border-amber-500/25 text-zinc-200 hover:text-amber-100',
            )}
          >
            <Bell className="h-3.5 w-3.5" />
            {unread > 0 && (
              <span
                aria-hidden
                className="absolute -top-0.5 -right-0.5 h-3.5 min-w-[14px] px-1 grid place-items-center rounded-full bg-red-500 text-white text-[8px] font-bold font-mono shadow-[0_0_8px_rgba(239,68,68,0.8)]"
              >
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </button>
        )}
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side={side}
          align={align}
          sideOffset={8}
          collisionPadding={8}
          className={cn(
            // Width is responsive — 360px desktop fits the dense dropdown
            // layout, but on phones we cap to 92vw so the panel doesn't
            // exceed the viewport (e.g. iPhone SE @ 320px would otherwise
            // overflow horizontally and clip the right-side actions).
            'z-[60] w-[min(360px,92vw)] rounded-md border border-amber-500/30 bg-[#0b0a08]/95 backdrop-blur-md shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-amber-500/15">
            <span className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.22em] text-amber-100">
              <Bell className="h-3.5 w-3.5" />
              Notifications
            </span>
            {unread > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  handleMarkAllRead();
                }}
                className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.18em] text-zinc-400 hover:text-amber-100"
              >
                <CheckCheck className="h-3 w-3" /> Mark read
              </button>
            )}
          </div>

          <div data-gild-scroll className="max-h-[360px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="py-8 text-center text-[10px] font-mono tracking-[0.22em] uppercase text-zinc-500">
                {isLive
                  ? "Nothing new · you're all caught up"
                  : 'Connect a wallet to see your live alerts'}
              </div>
            ) : (
              items.map((n) => {
                const navigable = !!n.navHref;
                return (
                  <DropdownMenu.Item
                    key={n.id}
                    // Activation = "open the row's destination". When a row
                    // has no in-app target (rare fallback), we suppress the
                    // default close so the menu doesn't disappear on a
                    // dead-end click. Action buttons in the row stop
                    // propagation, so they never reach this handler — the
                    // dropdown only navigates on a primary row click.
                    onSelect={(e) => {
                      if (!navigable) {
                        e.preventDefault();
                        return;
                      }
                      if (n.unread && n.numericId != null) {
                        // Fire-and-forget: keep the navigation snappy and
                        // let the optimistic update in `useNotifications`
                        // flip the row's unread badge before SSE reconciles.
                        void markAsRead(n.numericId);
                      }
                      router.push(n.navHref!);
                    }}
                    className={cn(
                      'flex items-start gap-2 px-3 py-2 border-b border-white/[0.04] outline-none transition-colors group',
                      n.unread ? 'bg-amber-500/[0.04]' : 'bg-transparent',
                      'hover:bg-white/[0.05]',
                      navigable ? 'cursor-pointer' : 'cursor-default',
                    )}
                  >
                    <NotificationRow n={n} />
                    <NotificationRowActions
                      notification={n}
                      onMarkRead={() => handleMarkRead(n.id, n.numericId)}
                      onMarkUnread={() => handleMarkUnread(n.id, n.numericId)}
                      onDelete={() => handleDelete(n.id, n.numericId)}
                    />
                  </DropdownMenu.Item>
                );
              })
            )}
          </div>

          <div className="border-t border-amber-500/15 bg-black/30">
            {pushUnsupported ? (
              <div className="flex items-center gap-2 px-3 py-2 text-[10px] text-zinc-500">
                <BellOff className="h-3.5 w-3.5" />
                Push not supported in this browser
              </div>
            ) : push.isIosNonPwa ? (
              <div className="px-3 py-2 text-[10px] text-zinc-400 leading-snug">
                On iOS, add Takeover to your Home Screen to enable push.
              </div>
            ) : (
              <DropdownMenu.Item
                onSelect={(e) => {
                  e.preventDefault();
                  togglePush();
                }}
                disabled={pushBlocked || push.isLoading}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 text-[11px] outline-none cursor-default',
                  pushBlocked
                    ? 'text-zinc-500 cursor-not-allowed'
                    : 'text-zinc-200 hover:text-amber-100 hover:bg-white/5',
                )}
              >
                {pushIsOn ? (
                  <Bell className="h-3.5 w-3.5 text-amber-200" />
                ) : (
                  <BellOff className="h-3.5 w-3.5" />
                )}
                <span>
                  {pushBlocked
                    ? 'Push blocked in browser'
                    : pushIsOn
                      ? 'Push notifications on'
                      : 'Enable push notifications'}
                </span>
                <span
                  aria-hidden
                  className={cn(
                    'ml-auto relative w-7 h-4 rounded-full transition-colors',
                    pushIsOn ? 'bg-amber-400/80' : 'bg-white/10',
                  )}
                >
                  <span
                    className={cn(
                      'absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform',
                      pushIsOn && 'translate-x-3',
                    )}
                  />
                </span>
              </DropdownMenu.Item>
            )}

            {/* Per-kind sub-toggles — only active while the master switch is on
               so users immediately see which channels will ping them. */}
            <div
              className={cn(
                'px-3 pb-2 pt-0.5 border-t border-amber-500/10 space-y-1 transition-opacity',
                pushIsOn ? 'opacity-100' : 'opacity-50',
              )}
            >
              <div className="flex items-center justify-between text-[8px] font-mono tracking-[0.28em] uppercase text-zinc-500 pt-1">
                <span>Notify me about</span>
                <span>{pushIsOn ? '' : 'Master off'}</span>
              </div>
              {(Object.keys(PUSH_KIND_META) as PushKind[]).map((kind) => {
                const meta = PUSH_KIND_META[kind];
                const on = pushKinds[kind];
                return (
                  <DropdownMenu.Item
                    key={kind}
                    disabled={!pushIsOn}
                    onSelect={(e) => {
                      e.preventDefault();
                      if (!pushIsOn) return;
                      togglePushKind(kind);
                    }}
                    className={cn(
                      'flex items-center gap-2 px-1.5 py-1.5 rounded-[3px] outline-none cursor-default',
                      pushIsOn
                        ? 'text-zinc-200 hover:bg-white/[0.04] hover:text-amber-100'
                        : 'text-zinc-500 cursor-not-allowed',
                    )}
                  >
                    <span
                      className={cn(
                        'grid place-items-center w-5 h-5 rounded-[3px] border border-white/10 bg-black/40 shrink-0',
                        meta.color,
                      )}
                    >
                      {meta.icon}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-[11px] font-medium truncate leading-tight">
                        {meta.label}
                      </span>
                      <span className="block text-[9px] font-mono tracking-[0.12em] uppercase text-zinc-500 leading-tight">
                        {meta.hint}
                      </span>
                    </span>
                    <span
                      aria-hidden
                      className={cn(
                        'relative w-7 h-4 rounded-full transition-colors shrink-0',
                        pushIsOn && on ? 'bg-amber-400/80' : 'bg-white/10',
                      )}
                    >
                      <span
                        className={cn(
                          'absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform',
                          pushIsOn && on && 'translate-x-3',
                        )}
                      />
                    </span>
                  </DropdownMenu.Item>
                );
              })}
            </div>
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
    {/* Surfaced once per device the first time a viewer flips push on.
        Sits as a sibling of the dropdown so it survives the dropdown
        closing on outside click — Radix dismisses the menu the moment
        the dialog overlay catches focus, but the dialog (mounted
        outside the menu's portal) stays up until the user picks an
        action. */}
    <PushBetaNoticeModal {...pushBetaModalProps} />
    </>
  );
}

// ─── Row renderer ──────────────────────────────────────────────────────────

function NotificationRow({ n }: { n: LobbyNotification }) {
  switch (n.kind) {
    case 'buyout':
      return <BuyoutRow n={n} />;
    case 'mention':
      return <MentionRow n={n} />;
    case 'expiry_1d':
    case 'expiry_1h':
      return <ExpiryRow n={n} />;
  }
}

interface RowActionsProps {
  notification: LobbyNotification;
  onMarkRead: () => void;
  onMarkUnread: () => void;
  onDelete: () => void;
}

function NotificationRowActions({
  notification,
  onMarkRead,
  onMarkUnread,
  onDelete,
}: RowActionsProps) {
  return (
    <div className="flex flex-col gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
      {notification.unread ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onMarkRead();
          }}
          className="p-0.5 rounded text-zinc-500 hover:text-amber-100 hover:bg-white/5 transition-colors"
          title="Mark as read"
        >
          <Eye className="h-3 w-3" />
        </button>
      ) : (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onMarkUnread();
          }}
          className="p-0.5 rounded text-zinc-500 hover:text-amber-100 hover:bg-white/5 transition-colors"
          title="Mark as unread"
        >
          <EyeOff className="h-3 w-3" />
        </button>
      )}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="p-0.5 rounded text-zinc-500 hover:text-red-300 hover:bg-red-500/10 transition-colors"
        title="Delete"
      >
        <Trash2 className="h-3 w-3" />
      </button>
    </div>
  );
}

function UnreadDot({ show }: { show?: boolean }) {
  if (!show) return null;
  return <span className="w-1.5 h-1.5 rounded-full bg-red-400 shadow-[0_0_6px_rgba(239,68,68,0.8)] shrink-0" />;
}

/**
 * Single avatar slot used by every row kind. We always lead with the
 * board's curated `pool_image_url` so the viewer can identify the
 * source board at a glance — the previous per-kind icons (sword /
 * triangle / chat-bubble) carried no board affinity and looked
 * uniform once you'd seen them once. When the board has no image we
 * deterministically generate one from its id so the slot still
 * renders something distinct per row instead of a stone-coloured
 * placeholder.
 */
function BoardAvatar({ board }: { board: BoardRef }) {
  const fallbackSrc = board.id ? generateGridAvatarUrl(board.id) : null;
  const src = board.imageUrl ?? fallbackSrc;
  if (src) {
    return (
      <span className="mt-0.5 shrink-0 w-6 h-6 grid place-items-center rounded-[3px] border border-white/10 bg-black/40 overflow-hidden">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={src}
          alt={board.name}
          className="w-full h-full object-cover"
        />
      </span>
    );
  }
  // Last-resort hue swatch — only hits when the row has no
  // `imageUrl` AND no `id` (pre-migration mock entries).
  return (
    <span
      className="mt-0.5 shrink-0 w-6 h-6 grid place-items-center rounded-[3px] border border-white/10"
      style={{ borderColor: board.hue + '60', backgroundColor: board.hue + '20' }}
      aria-label={board.name}
    />
  );
}

function BuyoutRow({ n }: { n: LobbyNotification }) {
  const isBulk = !!n.seats && n.seats.length > 1;
  const seatCount = n.seats?.length ?? 1;
  const preview = isBulk ? n.seats!.slice(0, 3).map((s) => `#${s}`).join(', ') : null;
  const overflow = isBulk ? n.seats!.length - 3 : 0;
  const buyoutLabel = isBulk
    ? `${seatCount} seats bought out`
    : n.seat
      ? `Seat ${n.seat} bought out`
      : 'Seat bought out';

  const { guardClick } = useExternalLinkGuard();

  return (
    <>
      <BoardAvatar board={n.board} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          {n.earned && (
            <span className="text-[11px] font-medium text-emerald-300 font-mono tabular-nums">
              {n.earned}
            </span>
          )}
          <span className="text-[11px] font-medium text-zinc-100 truncate">
            {buyoutLabel}
          </span>
          <UnreadDot show={n.unread} />
        </div>
        <div className="text-[10px] text-zinc-400 leading-snug truncate">
          {isBulk
            ? `${preview}${overflow > 0 ? ` +${overflow} more` : ''}${n.paid ? ` · paid ${n.paid}` : ''}`
            : `${n.actor ? `by ${n.actor}` : ''}${n.paid ? ` · paid ${n.paid}` : ''}`}
        </div>
        <div className="mt-1 flex items-center gap-1.5">
          <span className="text-[9px] text-zinc-600 font-mono">{n.ago} ago</span>
          {n.href && (
            <a
              href={n.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.stopPropagation();
                guardClick(n.href!)(e);
              }}
              className="ml-auto inline-flex items-center gap-0.5 text-[9px] text-blue-300 hover:text-blue-200 font-mono"
            >
              tx
              <ExternalLink className="h-2.5 w-2.5" />
            </a>
          )}
        </div>
      </div>
    </>
  );
}

function MentionRow({ n }: { n: LobbyNotification }) {
  return (
    <>
      <BoardAvatar board={n.board} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-medium text-sky-300 truncate">
            Mentioned by <span className="text-zinc-100">@{n.actor}</span>
          </span>
          <UnreadDot show={n.unread} />
        </div>
        {n.message && (
          <div className="text-[10px] text-zinc-400 leading-snug italic truncate">
            &ldquo;{n.message}&rdquo;
          </div>
        )}
        <div className="mt-1 flex items-center gap-1.5">
          <span className="text-[9px] text-zinc-600 font-mono">{n.ago} ago</span>
        </div>
      </div>
    </>
  );
}

function ExpiryRow({ n }: { n: LobbyNotification }) {
  const urgent = n.kind === 'expiry_1h';
  const tone = urgent ? 'text-red-400' : 'text-yellow-400';
  const headline = urgent ? '< 1h until expiry' : '< 24h until expiry';

  return (
    <>
      <BoardAvatar board={n.board} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className={cn('text-[11px] font-semibold font-mono tabular-nums', tone)}>
            {headline}
          </span>
          <UnreadDot show={n.unread} />
        </div>
        <div className="text-[10px] text-zinc-400 leading-snug truncate">
          {n.seat ? `Seat ${n.seat} · top up to keep the seat` : 'Top up to keep your seat'}
        </div>
        <div className="mt-1 flex items-center gap-1.5">
          <span className="text-[9px] text-zinc-600 font-mono">{n.ago} ago</span>
        </div>
      </div>
    </>
  );
}
